// The Home Key on-screen piano — presentational, G3 (55) to G5 (79),
// 25 keys. Controlled entirely by the parent: `activeMidis` lights keys,
// presses/releases are reported up via onPress/onRelease and sound nothing
// by themselves (useHomeKeyVoices does that).
//
// Props: {
//   activeMidis: Set<midi>,
//   onPress(midi), onRelease(midi),
//   highlight?: { homeMidis?: number[], scaleOnly?: boolean },
//   disabled?: boolean,
// }
//
// Every G is a home key (.hk-key--home) unless highlight.homeMidis narrows
// it. With highlight.scaleOnly, in-scale keys get .hk-key--scale plus their
// degree number; out-of-scale keys stay playable but unmarked (the CSS dims
// them) — outside the neighborhood, not forbidden.

import React, { useRef } from "react";
import { PIANO_RANGE, isInScale, degreeOf } from "../../lib/homeKeyTheory";
import { TYPING_KEY_BY_MIDI } from "./useTypingKeys";

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const HOME_PITCH_CLASS = 7; // G

// Key list with a running white-key index for layout; each black key carries
// the index of the white key to its left (same scheme as Number Sense).
export const PIANO_KEYS = (() => {
  const keys = [];
  let wi = 0;
  for (let midi = PIANO_RANGE.low; midi <= PIANO_RANGE.high; midi += 1) {
    const name = NOTE_NAMES[midi % 12];
    const black = name.includes("#");
    keys.push({
      midi,
      name,
      octave: Math.floor(midi / 12) - 1,
      black,
      wi: black ? wi - 1 : wi,
    });
    if (!black) wi += 1;
  }
  return keys;
})();

const WHITE_COUNT = PIANO_KEYS.filter((k) => !k.black).length;
const W = 100 / WHITE_COUNT; // % width per white key
const BW = W * 0.6; // black-key width

// Screen-reader-friendly name ("G sharp 3", not "G#3").
const spoken = (k) => `${k.name.replace("#", " sharp")} ${k.octave}`;

export default function Piano({
  activeMidis,
  onPress,
  onRelease,
  highlight = {},
  disabled = false,
}) {
  const downRef = useRef(new Set()); // midis this piano is currently holding
  const active = activeMidis || new Set();
  const homeSet = highlight.homeMidis ? new Set(highlight.homeMidis) : null;
  const isHome = (midi) =>
    homeSet ? homeSet.has(midi) : midi % 12 === HOME_PITCH_CLASS;

  const start = (midi) => {
    if (disabled || downRef.current.has(midi)) return;
    downRef.current.add(midi);
    if (onPress) onPress(midi);
  };
  const end = (midi) => {
    if (!downRef.current.has(midi)) return;
    downRef.current.delete(midi);
    if (onRelease) onRelease(midi);
  };

  const handlePointerDown = (midi) => (e) => {
    if (disabled) return;
    e.preventDefault();
    // Keep this pointer bound to its key so the matching up/cancel lands
    // back here even if the finger drifts off — no stuck notes. (Touch
    // already captures implicitly; make it explicit for mouse/pen.)
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        // stale pointer id — harmless
      }
    }
    start(midi);
  };
  const handleEnd = (midi) => () => end(midi);

  return (
    <div className="hk-piano" role="group" aria-label="on-screen piano, G3 to G5" style={{ position: "relative" }}>
      {PIANO_KEYS.map((k) => {
        const left = k.black ? (k.wi + 1) * W - BW / 2 : k.wi * W;
        const width = k.black ? BW : W;
        const inScale = isInScale(k.midi);
        let cls = "hk-key";
        if (k.black) cls += " hk-key--black";
        if (active.has(k.midi)) cls += " hk-key--active";
        if (isHome(k.midi)) cls += " hk-key--home";
        if (highlight.scaleOnly && inScale) cls += " hk-key--scale";
        const typing = TYPING_KEY_BY_MIDI[k.midi];
        const degree = highlight.scaleOnly && inScale ? degreeOf(k.midi) : null;
        return (
          <button
            key={k.midi}
            type="button"
            className={cls}
            style={{
              position: "absolute",
              left: `${left}%`,
              width: `${width}%`,
              touchAction: "none",
            }}
            disabled={disabled}
            aria-label={spoken(k)}
            aria-pressed={active.has(k.midi)}
            onPointerDown={handlePointerDown(k.midi)}
            onPointerUp={handleEnd(k.midi)}
            onPointerCancel={handleEnd(k.midi)}
            onPointerLeave={handleEnd(k.midi)}
            onKeyDown={(e) => {
              if ((e.key === " " || e.key === "Enter") && !e.repeat) {
                e.preventDefault();
                start(k.midi);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === " " || e.key === "Enter") end(k.midi);
            }}
          >
            {degree != null && (
              <span className="hk-key-degree" aria-hidden="true">
                {degree}
              </span>
            )}
            {typing && (
              <span className="hk-key-cap" aria-hidden="true">
                {typing === ";" || typing === "'" ? typing : typing.toUpperCase()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
